import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  Skeleton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  DeleteSweep as DeleteSweepIcon,
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  renamePushSubscription,
  NotificationPreference,
} from '@/services/notificationService';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAppSelector } from '@/hooks/redux';

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12:00 AM' : i === 12 ? '12:00 PM' : i < 12 ? `${i}:00 AM` : `${i - 12}:00 PM`,
}));

export const NotificationSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);
  const parentId = user?.parentId;
  const queryKey = useMemo(() => ['notification-preferences', parentId], [parentId]);
  // Optimistic overrides — only individual fields, cleared when server responds
  const [optimisticOverrides, setOptimisticOverrides] = useState<Partial<NotificationPreference>>({});
  const [disableAllDialogOpen, setDisableAllDialogOpen] = useState(false);
  const [deleteDeviceId, setDeleteDeviceId] = useState<string | null>(null);
  const [renameDevice, setRenameDevice] = useState<{ id: string; name: string } | null>(null);

  // Clear optimistic overrides when org changes
  useEffect(() => {
    setOptimisticOverrides({});
  }, [parentId]);

  // Fetch preferences
  const { data: preferences, isLoading: prefsLoading } = useQuery(
    queryKey,
    getNotificationPreferences,
  );

  // Update preferences mutation
  const updateMutation = useMutation(
    (updates: Partial<NotificationPreference>) => updateNotificationPreferences(updates),
    {
      onSuccess: (data) => {
        // Always update the correct org's cache using the response's parentId
        queryClient.setQueryData(['notification-preferences', data.parentId], data);
        // Clear optimistic overrides — server data is now authoritative
        if (data.parentId === parentId) {
          setOptimisticOverrides({});
        }
      },
    }
  );

  // Push notifications hook
  const {
    isSupported: pushSupported,
    isEnabled: pushEnabled,
    isSubscribed,
    permission,
    subscriptions,
    loading: pushLoading,
    error: pushError,
    subscribe,
    unsubscribe,
    refreshSubscriptions,
  } = usePushNotifications();

  const { isOnline } = useOnlineStatus();

  // Handle preference change
  const handleChange = useCallback(
    (field: keyof NotificationPreference, value: any) => {
      // Optimistic update for instant UI feedback
      setOptimisticOverrides((prev) => ({ ...prev, [field]: value }));

      // Debounce the API call
      setTimeout(() => {
        updateMutation.mutate({ [field]: value });
      }, 300);
    },
    [updateMutation]
  );

  // Handle push subscription
  const handleEnablePush = useCallback(async () => {
    const success = await subscribe();
    if (success) {
      handleChange('pushEnabled', true);
    }
  }, [subscribe, handleChange]);

  // Handle push unsubscribe
  const handleDisablePush = useCallback(async () => {
    await unsubscribe();
    handleChange('pushEnabled', false);
  }, [unsubscribe, handleChange]);

  // Handle remove subscription
  const handleRemoveSubscription = useCallback(
    async (id: string) => {
      await unsubscribe(id);
    },
    [unsubscribe]
  );

  // Handle rename subscription
  const handleRenameSubscription = useCallback(
    async (id: string, name: string) => {
      await renamePushSubscription(id, name);
      await refreshSubscriptions();
    },
    [refreshSubscriptions]
  );

  const handleCustomizedToggle = useCallback(
    (customized: boolean) => {
      setOptimisticOverrides((prev) => ({ ...prev, customized }));
      updateMutation.mutate({ customized }, {
        onSuccess: async (data) => {
          // Push subscriptions are copied/removed on the server when toggling,
          // so refresh the device list
          await refreshSubscriptions();

          // When un-customizing: if global settings have push enabled and this
          // device has a browser push subscription, auto-register it for this org
          if (!customized && data.pushEnabled && !isSubscribed) {
            await subscribe();
          }
        },
      });
    },
    [updateMutation, refreshSubscriptions, isSubscribed, subscribe]
  );

  const hasMultipleOrgs = (user?.availableOrganizations?.length ?? 0) > 1;
  const currentOrgName = user?.availableOrganizations?.find(
    (org) => org.organizationId === user?.currentOrganizationId
  )?.organizationName;

  // Merge server data with optimistic overrides
  const prefs = preferences ? { ...preferences, ...optimisticOverrides } : undefined;

  if (prefsLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Customize for this org toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={prefs?.customized || false}
              onChange={(e) => handleCustomizedToggle(e.target.checked)}
              disabled={!hasMultipleOrgs}
            />
          }
          label="Customize for this organization"
        />
        <Typography variant="body2" color="text.secondary">
          {!hasMultipleOrgs
            ? 'Join multiple organizations to customize settings per org'
            : prefs?.customized
              ? `Settings only apply to ${currentOrgName || 'this organization'}`
              : 'Settings apply to all your organizations'}
        </Typography>
      </Paper>

      {/* Email Notifications */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Notifications
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={prefs?.emailEnabled || false}
              onChange={(e) => handleChange('emailEnabled', e.target.checked)}
            />
          }
          label="Enable email notifications"
        />

        {prefs?.emailEnabled && (
          <Box sx={{ mt: 2, pl: 2 }}>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Delivery Mode</FormLabel>
              <RadioGroup
                value={prefs?.emailDigestMode || 'immediate'}
                onChange={(e) => handleChange('emailDigestMode', e.target.value)}
              >
                <FormControlLabel
                  value="immediate"
                  control={<Radio size="small" />}
                  label="Immediate - Send emails right away"
                />
                <FormControlLabel
                  value="daily"
                  control={<Radio size="small" />}
                  label="Daily digest - One email per day"
                />
                <FormControlLabel
                  value="weekly"
                  control={<Radio size="small" />}
                  label="Weekly digest - One email per week (Sundays)"
                />
              </RadioGroup>
            </FormControl>

            {(prefs?.emailDigestMode === 'daily' || prefs?.emailDigestMode === 'weekly') && (
              <FormControl size="small" sx={{ mb: 2, minWidth: 150 }}>
                <FormLabel>Preferred time</FormLabel>
                <Select
                  value={prefs?.emailDigestHour || 9}
                  onChange={(e) => handleChange('emailDigestHour', e.target.value)}
                >
                  {HOURS.map((hour) => (
                    <MenuItem key={hour.value} value={hour.value}>
                      {hour.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Email me about:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnDateChange ?? true}
                    onChange={(e) => handleChange('emailOnDateChange', e.target.checked)}
                  />
                }
                label="Date changes"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnTimeChange ?? true}
                    onChange={(e) => handleChange('emailOnTimeChange', e.target.checked)}
                  />
                }
                label="Time changes"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnLocationChange ?? true}
                    onChange={(e) => handleChange('emailOnLocationChange', e.target.checked)}
                  />
                }
                label="Location changes"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnStatusChange ?? true}
                    onChange={(e) => handleChange('emailOnStatusChange', e.target.checked)}
                  />
                }
                label="Status changes (TBD, scheduled, postponed)"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnConflict ?? true}
                    onChange={(e) => handleChange('emailOnConflict', e.target.checked)}
                  />
                }
                label="Schedule conflicts"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnNewMatch ?? true}
                    onChange={(e) => handleChange('emailOnNewMatch', e.target.checked)}
                  />
                }
                label="New matches added"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnSyncComplete ?? true}
                    onChange={(e) => handleChange('emailOnSyncComplete', e.target.checked)}
                  />
                }
                label="League schedule update summaries"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={prefs?.emailOnScorePosted ?? true}
                    onChange={(e) => handleChange('emailOnScorePosted', e.target.checked)}
                  />
                }
                label="Score posted"
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Push Notifications */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Push Notifications
          {!pushSupported && (
            <Chip label="Not supported" size="small" color="default" sx={{ ml: 1 }} />
          )}
        </Typography>

        {!pushSupported ? (
          <Alert severity="info" icon={<InfoIcon />}>
            Push notifications are not supported in this browser. Try using Chrome, Firefox, or Safari on
            a supported device.
          </Alert>
        ) : !pushEnabled ? (
          <Alert severity="info" icon={<InfoIcon />}>
            Push notifications are not enabled on this server. Contact your administrator to enable them.
          </Alert>
        ) : (
          <>
            {pushError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pushError}
              </Alert>
            )}

            {permission === 'denied' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Notification permission was denied. Please enable notifications in your browser settings
                to receive push notifications.
              </Alert>
            )}

            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Tooltip title={isSubscribed ? 'This device is already added' : 'Add this device for push notifications'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleEnablePush}
                      disabled={isSubscribed || pushLoading || permission === 'denied'}
                    >
                      Add This Device
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Added Devices</Typography>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDisableAllDialogOpen(true)}
                disabled={pushLoading || subscriptions.length === 0 || !isOnline}
                sx={{ mb: 1 }}
              >
                Delete All
              </Button>

              {subscriptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No devices added
                </Typography>
              ) : (
                <List dense>
                  {subscriptions.map((sub, index) => (
                    <ListItem key={sub.id} divider={index < subscriptions.length - 1} sx={{ px: 0 }}>
                      <ListItemText
                        primary={sub.deviceName || 'Unknown Device'}
                        secondary={`Added ${formatDistanceToNow(new Date(sub.createdAt), {
                          addSuffix: true,
                        })}`}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => setRenameDevice({ id: sub.id, name: sub.deviceName || '' })}
                          disabled={!isOnline}
                        >
                          Rename
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteDeviceId(sub.id)}
                          disabled={!isOnline}
                        >
                          Delete
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}

              {subscriptions.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Notify me for:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnDateChange ?? true}
                          onChange={(e) => handleChange('pushOnDateChange', e.target.checked)}
                        />
                      }
                      label="Date changes"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnTimeChange ?? true}
                          onChange={(e) => handleChange('pushOnTimeChange', e.target.checked)}
                        />
                      }
                      label="Time changes"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnLocationChange ?? true}
                          onChange={(e) => handleChange('pushOnLocationChange', e.target.checked)}
                        />
                      }
                      label="Location changes"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnStatusChange ?? true}
                          onChange={(e) => handleChange('pushOnStatusChange', e.target.checked)}
                        />
                      }
                      label="Status changes"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnConflict ?? true}
                          onChange={(e) => handleChange('pushOnConflict', e.target.checked)}
                        />
                      }
                      label="Schedule conflicts"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnNewMatch ?? true}
                          onChange={(e) => handleChange('pushOnNewMatch', e.target.checked)}
                        />
                      }
                      label="New matches added"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnSyncComplete ?? true}
                          onChange={(e) => handleChange('pushOnSyncComplete', e.target.checked)}
                        />
                      }
                      label="League schedule update summaries"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={prefs?.pushOnScorePosted ?? true}
                          onChange={(e) => handleChange('pushOnScorePosted', e.target.checked)}
                        />
                      }
                      label="Score posted"
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* Disable All Confirmation Dialog */}
            <Dialog
              open={disableAllDialogOpen}
              onClose={() => setDisableAllDialogOpen(false)}
            >
              <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteSweepIcon />Delete All Push Notifications?</Box></DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure? This will remove all added devices and delete push notifications.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
                <Button variant="outlined" onClick={() => setDisableAllDialogOpen(false)} startIcon={<CancelIcon />}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setDisableAllDialogOpen(false);
                    handleDisablePush();
                  }}
                >
                  I'm Sure
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Device Confirmation Dialog */}
            <Dialog
              open={deleteDeviceId !== null}
              onClose={() => setDeleteDeviceId(null)}
            >
              <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneAndroidIcon />Delete Device?</Box></DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to remove{' '}
                  <strong>{subscriptions.find(s => s.id === deleteDeviceId)?.deviceName || 'this device'}</strong>?
                  It will no longer receive push notifications.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
                <Button variant="outlined" onClick={() => setDeleteDeviceId(null)} startIcon={<CancelIcon />}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    const id = deleteDeviceId;
                    setDeleteDeviceId(null);
                    if (id) handleRemoveSubscription(id);
                  }}
                >
                  I'm Sure
                </Button>
              </DialogActions>
            </Dialog>

            {/* Rename Device Dialog */}
            <Dialog
              open={renameDevice !== null}
              onClose={() => setRenameDevice(null)}
            >
              <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon />Rename Device</Box></DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label="Device name"
                  value={renameDevice?.name || ''}
                  onChange={(e) => setRenameDevice(prev => prev ? { ...prev, name: e.target.value } : null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renameDevice?.name.trim()) {
                      const { id, name } = renameDevice;
                      setRenameDevice(null);
                      handleRenameSubscription(id, name.trim());
                    }
                  }}
                  inputProps={{ maxLength: 100 }}
                  sx={{ mt: 1 }}
                />
              </DialogContent>
              <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
                <Button variant="outlined" onClick={() => setRenameDevice(null)} startIcon={<CancelIcon />}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  disabled={!renameDevice?.name.trim()}
                  onClick={() => {
                    if (renameDevice?.name.trim()) {
                      const { id, name } = renameDevice;
                      setRenameDevice(null);
                      handleRenameSubscription(id, name.trim());
                    }
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default NotificationSettings;
